import { useState, createContext, useContext, useEffect, useCallback, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import noop from 'lodash/noop';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { AuthContext } from '@/contexts/AuthContext';
import { useAuthConfig } from '@/hooks/useAuthConfig';
import { BASE_SERVER_URL } from '@/globals';
import { ClientToServerEvents, ServerToClientEvents } from '@/types/api/socket';
import { HelloEventData } from '@/types/api/socket/hello-example-event';
import { log } from '@/logger/logger.ts';
import { reportSocketHealth } from '@/utils/socketHealth';
import { useUserWorkflowRole, useWorkflowStore } from '@/state/workflow.state';
import { FF_LOG_SOCKET_EVENTS, FF_SOCKET_RECONNECTION_ISSUE } from '@/consts/featureFlags';

const logger = log.getLogger('SocketContext');

const WORKFLOW_NAMESPACE = '/workflow';

const TOKEN_EXPIRY_CLOSE_WINDOW_MS = 60_000;

const tokenExpiresWithin = (token: string, windowMs: number): boolean => {
  try {
    const { exp } = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return typeof exp === 'number' && exp * 1000 - Date.now() <= windowMs;
  } catch {
    return false;
  }
};

/**
 * Stable identifier for this browser tab, generated once per page load and held
 * in memory (not localStorage, which is shared across tabs). It survives socket
 * recreations (e.g. token refresh), so the server can tell a reconnecting tab
 * apart from a genuinely separate tab.
 */
const TAB_ID = crypto.randomUUID();

const SocketContext = createContext<{
  sendHello: (event: HelloEventData) => void;
  addListener: <T extends keyof ListenerMap>(event: T, listener: ListenerMap[T][number]) => () => void;
  getIsConnected: () => boolean;
  recreateSocket: () => void;
  /** Runs `listener` with `true` each time the socket connects (the first time too) and `false` each time it drops. */
  addConnectionListener: (listener: (isConnected: boolean) => void) => () => void;
}>({
  sendHello: noop,
  addListener: () => noop,
  getIsConnected: () => false,
  recreateSocket: noop,
  addConnectionListener: () => noop,
});

// Events dispatched through the consumer-facing listener registry. `tab_probe`
// and `edit_blocked` are handled internally in initListeners, so they are not
// part of this map.
type RegistryEventName = 'batch_run_status' | 'graph_changed';

type ListenerMap = {
  [key in RegistryEventName]: ServerToClientEvents[key][];
};

export const SocketProvider = ({ children }) => {
  const [socket, setSocket] = useState<Socket<ServerToClientEvents, ClientToServerEvents> | null>(null);
  const [recreateTrigger, setRecreateTrigger] = useState(0);
  const connectedSinceRef = useRef<number | null>(null);
  const disconnectedSinceRef = useRef<number | null>(null);
  const lastServerMessageAtRef = useRef<number | null>(null);
  const markServerMessage = useCallback(() => {
    lastServerMessageAtRef.current = Date.now();
  }, []);
  const listeners = useRef<ListenerMap>({
    batch_run_status: [],
    graph_changed: [],
  });
  // Recreating a socket removes its listeners before disconnecting it, so a token refresh never
  // reaches these as a drop.
  const connectionListeners = useRef<Array<(isConnected: boolean) => void>>([]);
  const { accessToken, authApi } = useContext(AuthContext);
  // `authApi` is a new object on most auth renders; read it through a ref so the socket is not rebuilt.
  const authApiRef = useRef(authApi);
  useEffect(() => {
    authApiRef.current = authApi;
  }, [authApi]);
  const { provider } = useAuthConfig();
  const role = useUserWorkflowRole();
  const recipeId = useWorkflowStore((state) => state.recipe?.id);
  const FF_isSocketReconnectionIssue = useFeatureFlagEnabled(FF_SOCKET_RECONNECTION_ISSUE);
  const FF_isLogSocketEvents = useFeatureFlagEnabled(FF_LOG_SOCKET_EVENTS);

  const addListener = useCallback(<T extends keyof ListenerMap>(event: T, listener: ListenerMap[T][number]) => {
    const currentListeners = listeners.current[event];
    listeners.current[event] = [...currentListeners, listener] as ListenerMap[T];
    return () => {
      const newListeners = listeners.current[event].filter((l) => l !== listener);
      listeners.current[event] = newListeners as ListenerMap[T];
    };
  }, []);

  const initListeners = useCallback(
    (socket: Socket<ServerToClientEvents, ClientToServerEvents>, token: string) => {
      socket.on('connect', () => {
        if (FF_isLogSocketEvents || FF_isSocketReconnectionIssue) {
          logger.debug('Socket: connect', `recovered: ${socket.recovered}`);
        }
        if (FF_isLogSocketEvents) {
          const downSince = disconnectedSinceRef.current;
          disconnectedSinceRef.current = null;
          reportSocketHealth('socket_connected', {
            // Absent on the first connect of a session; present means this was a reconnection, and
            // the value is how long result delivery had no socket behind it.
            downtimeMs: downSince === null ? undefined : Date.now() - downSince,
          });
        }
        connectedSinceRef.current = Date.now();
        markServerMessage();
        connectionListeners.current.forEach((listener) => listener(true));
      });
      socket.onAny(markServerMessage);
      socket.io.on('ping', markServerMessage);
      socket.on('connect_error', (error) => {
        if (FF_isLogSocketEvents || FF_isSocketReconnectionIssue) {
          logger.debug('Socket: connect_error', { error });
        }
        if (FF_isLogSocketEvents) {
          reportSocketHealth('socket_connect_error', { message: error.message });
        }
      });
      socket.on('disconnect', (reason, description) => {
        if (FF_isLogSocketEvents || FF_isSocketReconnectionIssue) {
          logger.debug(`Socket: disconnected. Reason: ${reason}`, { description });
        }
        connectionListeners.current.forEach((listener) => listener(false));
        if (FF_isLogSocketEvents) {
          const up = connectedSinceRef.current;
          disconnectedSinceRef.current = Date.now();
          connectedSinceRef.current = null;
          // `reason` is the field that matters: socket.io reconnects itself for 'transport close'
          // and 'ping timeout', but not for 'io server disconnect', which needs the recreate path.
          const lastServerMessageAt = lastServerMessageAtRef.current;
          reportSocketHealth('socket_disconnected', {
            reason,
            description: description instanceof Error ? description.message : description?.description,
            connectedForMs: up === null ? undefined : Date.now() - up,
            visibility: document.visibilityState,
            online: navigator.onLine,
            // The server pings every 25s, so a gap well past that means the connection was dead before
            // the browser reported it.
            serverSilentForMs: lastServerMessageAt === null ? undefined : Date.now() - lastServerMessageAt,
          });
        }
        // The server closes a socket at token expiry; a fresh token changes `accessToken`, which reopens it.
        if (reason === 'io server disconnect' && tokenExpiresWithin(token, TOKEN_EXPIRY_CLOSE_WINDOW_MS)) {
          authApiRef.current
            .getCurrentUser()
            ?.getIdToken(true)
            .catch((error) => logger.warn('Socket: token refresh after expiry close failed', { error }));
        }
        // if (reason === 'io server disconnect') {
        //   if (isLoggingSocketEvents) {
        //     logger.info('Socket: reconnecting on io server disconnect');
        //   }
        //   try {
        //     socket.connect();
        //   } catch (error) {
        //     logger.error('Error connecting socket after disconnect', error);
        //   }
        // }
      });
      socket.on('batch_run_status', (data) => {
        if (FF_isLogSocketEvents || FF_isSocketReconnectionIssue) {
          logger.debug('Socket: batch_run_status', data);
        }
        listeners.current.batch_run_status.forEach((listener) => listener(data));
      });
      socket.on('graph_changed', (data) => {
        if (FF_isLogSocketEvents || FF_isSocketReconnectionIssue) {
          logger.debug('Socket: graph_changed', data);
        }
        listeners.current.graph_changed.forEach((listener) => listener(data));
      });
      socket.on('edit_blocked', (data) => {
        if (FF_isLogSocketEvents || FF_isSocketReconnectionIssue) {
          logger.debug('Socket: edit_blocked', data);
        }
        // Ignore our own takeover broadcast: a reconnecting tab's demote reaches
        // its own prior socket, which must not put this tab into read-only.
        if (data?.fromTabId === TAB_ID) {
          return;
        }
        // Another tab took over editing. useUserWorkflowRole() derives a
        // read-only 'guest' role from this (which also tears down this socket via
        // the role gate, so this tab won't reconnect until refreshed), and
        // SaveErrorSnackbar surfaces the multi-tab message.
        logger.info('Tab locked: another tab is editing this workflow', {
          workflowId: useWorkflowStore.getState().recipe?.id,
          // The server names the tab that took over; the background-tab check names none.
          reason: data?.fromTabId ? 'takeover' : 'background',
          visibility: document.visibilityState,
        });
        useWorkflowStore.getState().setWorkflowError('MultiTab');
      });
      socket.on('tab_probe', (ack) => ack({ tabId: TAB_ID }));
    },
    [FF_isLogSocketEvents, FF_isSocketReconnectionIssue, markServerMessage],
  );

  const destroyListeners = useCallback(
    (socket: Socket<ServerToClientEvents, ClientToServerEvents>) => {
      socket.off('connect');
      socket.off('connect_error');
      socket.off('disconnect');
      socket.off('batch_run_status');
      socket.off('graph_changed');
      socket.off('edit_blocked');
      socket.off('tab_probe');
      socket.offAny(markServerMessage);
      socket.io.off('ping', markServerMessage);
    },
    [markServerMessage],
  );

  const recreateSocket = useCallback(() => {
    if (FF_isLogSocketEvents || FF_isSocketReconnectionIssue) {
      logger.debug('Recreating socket completely');
    }
    setRecreateTrigger((prev) => prev + 1);
  }, [FF_isLogSocketEvents, FF_isSocketReconnectionIssue]);

  useEffect(() => {
    if (!accessToken || role === 'guest' || !recipeId) {
      return;
    }

    if (FF_isLogSocketEvents || FF_isSocketReconnectionIssue) {
      logger.debug('Socket: Creating new socket connection with fresh token');
    }

    const workflowSocket = io(BASE_SERVER_URL + WORKFLOW_NAMESPACE, {
      withCredentials: true,
      auth: {
        token: accessToken,
        provider,
        tabId: TAB_ID,
        // Whether this tab is focused at connect. A background tab (cmd-click,
        // session restore) connects hidden and defers to the active tab instead
        // of stealing editing from it.
        visible: document.visibilityState === 'visible',
      },
      query: { recipeId },
      transports: ['websocket'],
    });
    initListeners(workflowSocket, accessToken);
    setSocket(workflowSocket);

    return () => {
      destroyListeners(workflowSocket);
      workflowSocket.disconnect();
      setSocket(null);
    };
    // eslint-disable-next-line react-hooks-js/exhaustive-deps
  }, [accessToken, destroyListeners, initListeners, provider, recipeId, role, recreateTrigger]);

  // Example
  const sendHello = useCallback((data: HelloEventData) => socket?.emit('hello', data), [socket]);

  const getIsConnected = useCallback(() => {
    return socket?.connected ?? false;
  }, [socket]);

  const addConnectionListener = useCallback((listener: (isConnected: boolean) => void) => {
    connectionListeners.current = [...connectionListeners.current, listener];
    return () => {
      connectionListeners.current = connectionListeners.current.filter((l) => l !== listener);
    };
  }, []);

  return (
    <SocketContext.Provider
      value={{
        sendHello,
        addListener,
        getIsConnected,
        recreateSocket,
        addConnectionListener,
      }}
    >
      {children}
    </SocketContext.Provider>
  );
};

export const useSocket = () => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within SocketProvider');
  }
  return context;
};
